/**
 * CR-6387: error handling for the NCR create screen.
 *
 * Turns a backend NCR-create failure into a human-readable message plus a
 * per-field error map, so the UI can explain WHY creation failed and
 * highlight the offending fields instead of a bare "Failed to create NCR".
 * The non-field message reuses the shared Helix envelope extractor
 * (`apiErrorMessage`); only the NCR-specific field-map + labelling lives here.
 */
import { apiErrorMessage } from '../../lib/api';
import { decodeApiText } from '../../utils/errorUtils';

/** Standardised API error body (see backend core/exception_handler.py). */
export interface ApiErrorBody {
  message?: string;
  detail?: string;
  details?: { fields?: Record<string, unknown> };
}

/** Friendly labels for backend field names, used in surfaced messages. */
export const NCR_FIELD_LABELS: Record<string, string> = {
  supplier: 'Supplier',
  source_of_rejection_option: 'Source of Rejection',
  fault_group: 'Fault Group',
  fault_category: 'Fault Category',
  defect_description: 'Defect Description',
  liability_type: 'Liability',
  // Liability Yes/No action fields (tri-state selects).
  is_8d_required: '8D Required',
  is_collection_required: 'Collection Required',
  is_credit_required: 'Credit Required',
  is_replacement_required: 'Replacement Required',
  containment_required: 'Stock Containment',
  containment_qty_ok: 'Containment OK Qty',
  containment_qty_not_ok: 'Containment NOT OK Qty',
  containment_current_stock: 'Current Stock',
  ncr_number: 'Company NCR/SRN No.',
  part_number: 'Part No.',
  unit_price: 'Unit Price',
  batch_qty: 'Batch Qty',
  reject_qty: 'Reject Qty',
  shortage_qty: 'Shortage Qty',
  assigned_to: 'Assigned To',
  // CR-8706: the rest of the NCR Detail fields, for the edit page's Save.
  grn_number: 'GRN No.',
  po_number: 'PO No.',
  description: 'Part Description',
  batch_number: 'Date Code / Batch No.',
  works_order: 'Works Order No.',
  buyer_name: 'Buyer',
  warehouse: 'Warehouse',
  customer_reference: 'Customer NCR No.',
  severity: 'Severity',
  proposed_rejection_type: 'Proposed Disposition',
  disposition_quantity: 'Disposition Qty',
  action_required: 'Action Required',
  liability_classification: 'Liability Classification',
  is_internal: 'Handle internally',
};

/** DRF's wording for a missing value, whatever the field type. */
const EMPTY_VALUE_MESSAGES = new Set([
  'This field may not be null.',
  'This field may not be blank.',
  'This field is required.',
  'A valid integer is required.',
  'A valid number is required.',
]);

/** The on-screen label for a backend NCR field name; `overrides` holds a
 *  screen's own wording where it differs from the create form's. */
export function ncrFieldLabel(key: string, overrides: Record<string, string> = {}): string {
  return overrides[key] || NCR_FIELD_LABELS[key] || key.replace(/_/g, ' ');
}

/**
 * CR-8706: one field's refusal as the user should read it.
 *
 * A missing value reads "{label} cannot be empty"; any other message is
 * prefixed with the label unless it already starts with it, so the cap's
 * "Reject Qty cannot be more than..." is not doubled. HTML entities from the
 * API's escaping are decoded.
 *
 * @param key - backend field name, e.g. `reject_qty`
 * @param message - the field's message from `details.fields`
 * @param labels - the screen's own labels, where they differ
 * @returns The message naming the field
 */
export function ncrFieldMessage(key: string, message: string, labels: Record<string, string> = {}): string {
  const text = decodeApiText(message);
  const label = ncrFieldLabel(key, labels);
  if (EMPTY_VALUE_MESSAGES.has(text)) return `${label} cannot be empty`;
  // DRF's min / max value wording, in the same "{label} cannot..." form.
  const min = /^Ensure this value is greater than or equal to (\S+?)\.$/.exec(text);
  if (min) return Number(min[1]) === 0 ? `${label} cannot be negative` : `${label} cannot be less than ${min[1]}`;
  const max = /^Ensure this value is less than or equal to (\S+?)\.$/.exec(text);
  if (max) return `${label} cannot be more than ${max[1]}`;
  return text.startsWith(label) ? text : `${label}: ${text}`;
}

/**
 * Normalise a single field's error to a safe string. DRF can nest dicts/lists
 * under `details.fields`, and an empty list stringifies to "undefined" while an
 * object stringifies to "[object Object]" — neither is useful, so unknown
 * shapes fall back to a generic message.
 */
function normalizeFieldMessage(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (Array.isArray(value)) {
    const firstString = value.find((v) => typeof v === 'string' && v.trim());
    if (firstString) return String(firstString);
  }
  return 'Invalid value.';
}

/**
 * Build `{ message, fields }` from a thrown NCR-create error.
 * @param error - the thrown error (usually an AxiosError)
 */
export function extractNcrError(error: unknown): { message: string; fields: Record<string, string> } {
  const resp = (error as { response?: { data?: ApiErrorBody; status?: number } })?.response;
  const rawFields = resp?.data?.details?.fields;

  const fields: Record<string, string> = {};
  let nonFieldMessage: string | undefined;
  // Only dict-like field maps: arrays also satisfy `typeof === 'object'` and
  // would be iterated into meaningless numeric keys.
  if (rawFields && typeof rawFields === 'object' && !Array.isArray(rawFields)) {
    for (const [key, value] of Object.entries(rawFields)) {
      if (key === 'non_field_errors') {
        // Form-level error — there is no input to highlight, so surface it as
        // the message rather than a labelled, uneditable field entry.
        nonFieldMessage = normalizeFieldMessage(value);
        continue;
      }
      fields[key] = decodeApiText(normalizeFieldMessage(value));
    }
  }

  const firstKey = Object.keys(fields)[0];
  if (firstKey) {
    return { message: ncrFieldMessage(firstKey, fields[firstKey] ?? ''), fields };
  }
  if (nonFieldMessage) {
    return { message: nonFieldMessage, fields };
  }
  // No highlightable field errors — reuse the shared envelope reader so any
  // backend-provided {detail}/{message} (including on 5xx) still surfaces,
  // falling back only when the response carries nothing useful.
  return {
    message: apiErrorMessage(error, 'Could not create the NCR. Please review the form and try again.'),
    fields,
  };
}
